<template>
  <div class="sf-page">
    <div class="sf-container sf-new">
      <top-image-header gradient="to right, rgba(11, 43, 87, .94) 0%, rgba(11, 43, 87, .7) 50%, rgba(11, 43, 87, .25) 100%"
                        image-src="/images/header8.jpg"
                        v-bind:aspect-ratio="3.4">
        <div class="sf-new__hero">
          <div class="sf-new__eyebrow">SAGASSER Hausmesse 2027</div>
          <h1 class="sf-hero__title white--text">{{ $t('newProducts') }}</h1>
          <p class="sf-new__lead"
             v-if="!loading">
            {{ $t('summary', { count: groups.reduce((n, g) => n + g.products.length, 0), suppliers: groups.length }) }}
          </p>
        </div>
      </top-image-header>

      <v-progress-linear class="mt-4"
                         color="accent"
                         indeterminate
                         v-bind:active="loading" />

      <sticky-bar class="sf-new__toolbar">
        <v-card class="sf-card"
                flat>
          <div class="sf-toolbar__row">
            <v-text-field class="sf-search sf-toolbar__search"
                          clearable
                          dense
                          flat
                          hide-details
                          prepend-inner-icon="ti-search"
                          solo
                          v-bind:placeholder="$t('search')"
                          v-model="search" />
            <span class="sf-toolbar__hint hidden-xs-only"
                  v-if="!loading">
              {{ $t('counts', { count: groups.reduce((n, g) => n + g.products.length, 0), suppliers: groups.length }) }}
            </span>
          </div>
          <v-divider />
          <div class="sf-new__jump"
               v-if="groups.length > 1">
            <span class="sf-new__jump-label">{{ $t('jumpTo') }}</span>
            <div class="sf-new__jump-chips">
              <v-chip class="sf-pill sf-pill--neutral sf-new__jump-chip"
                      small
                      v-bind:key="`${group.supplierId}-jump`"
                      v-for="group in groups"
                      v-on:click="$vuetify.goTo(`#supplier-${group.supplierId}`, { offset: 24 })">
                {{ group.supplier.supplierGroup | defaultValue(group.supplier.companyName) }}
              </v-chip>
            </div>
          </div>
        </v-card>
      </sticky-bar>

      <v-card class="sf-card mt-6"
              flat
              v-if="!loading && groups.length === 0">
        <v-card-text class="sf-card__body text-center sf-muted">
          {{ $t('noResults') }}
        </v-card-text>
      </v-card>

      <section class="sf-new__group"
               v-bind:id="`supplier-${group.supplierId}`"
               v-bind:key="`${group.supplierId}-group`"
               v-for="group in groups">
        <div class="sf-new__group-head">
          <router-link class="sf-new__supplier"
                       v-bind:to="{ name: 'supplier', params: { id: group.supplierId } }">
            <img class="sf-new__logo"
                 v-bind:alt="group.supplier.supplierGroup"
                 v-bind:src="logoUrl(group.supplier.logoId)"
                 v-if="group.supplier.logoId != null">
            <h2 class="sf-new__title"
                v-else>
              {{ group.supplier.supplierGroup | defaultValue(group.supplier.companyName) }}
            </h2>
          </router-link>
          <div class="sf-new__group-meta">
            <v-chip class="sf-pill sf-pill--success"
                    small
                    v-if="group.supplier.hasDeliveryPeriod0Orders">
              {{ group.supplier.deliveryPeriod0Tag | defaultValue($t('period1')) }}
            </v-chip>
            <v-chip class="sf-pill sf-pill--success"
                    small
                    v-if="group.supplier.hasDeliveryPeriod1Orders">
              {{ group.supplier.deliveryPeriod1Tag | defaultValue($t('period2')) }}
            </v-chip>
            <span class="sf-muted sf-new__group-count">{{ $tc('productCount', group.products.length, { count: group.products.length }) }}</span>
            <v-btn color="primary"
                   small
                   text
                   v-bind:to="{ name: 'supplier', params: { id: group.supplierId } }">
              {{ $t('toOrderSheet') }}
              <v-icon right
                      small>ti-arrow-right</v-icon>
            </v-btn>
          </div>
        </div>

        <product-grid v-bind:products="group.products" />
      </section>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  import settings from '../settings'

  import TopImageHeader from '@/components/common/TopImageHeader'
  import ProductGrid from '@/components/ProductGrid'
import StickyBar from '@/components/common/StickyBar'

  export default {
    components: {
      TopImageHeader,
      ProductGrid,
      StickyBar
    },
    computed: {
      filteredProducts() {
        if (this.search != null && this.search.count !== 0) {
          const search = this.search.toLowerCase()

          return this.products.filter(p => p.title.toLowerCase().includes(search) || p.supplierCompanyName.toLowerCase().includes(search))
        }

        return this.products
      },
      groups() {
        return this.sortedProducts.reduce((result, current) => {
          const supplier = this.supplier(current.supplierId)
          if (!supplier) return result

          const index = result.findIndex(p => p.supplierId === current.supplierId)

          if (index !== -1) {
            result[index].products.push({ ...current })
          } else {
            result.push({
              supplierId: current.supplierId,
              supplier,
              products: [{ ...current }]
            })
          }

          return result
        }, []).sort((a, b) => (a.supplier.supplierGroup ?? a.supplier.companyName).localeCompare(b.supplier.supplierGroup ?? b.supplier.companyName))
      },
      sortedProducts() {
        return this.filteredProducts.filter(p => p.new && (p.label === 'NEU' || p.label === 'UMSTELLUNG')).map(p => ({
          ...p,
          productPrice: this.formatPrice(9.99)
        }))
      },
      ...mapGetters([
        'customerId',
        'products',
        'supplier'
      ])
    },
    async created() {
      this.loading = true

      await this.$store.dispatch('loadSuppliersForMe')
      await this.$store.dispatch('loadProductsForMeNew')

      this.loading = false
    },
    data: () => ({
      loading: false,
      search: null
    }),
    methods: {
      logoUrl(id) {
        return `${settings.apiUri}/attachment/${id}/content`
      },
      formatPrice(price) {
        if (price == null || price <= 0) {
          return 'n. v.'
        }

        return price.toLocaleString(this.$i18n.locale, {
          style: 'currency',
          currency: 'EUR'
        })
      }
    },
    name: 'NewProducts'
  }
</script>

<style scoped>
  .sf-new {
    padding-top: 24px;
  }

  .sf-new__hero {
    max-width: 560px;
  }

  .sf-new__eyebrow {
    color: rgba(255, 255, 255, .85);
    font-size: .8125rem;
    font-weight: 700;
    letter-spacing: .12em;
    margin-bottom: 6px;
    text-transform: uppercase;
  }

  .sf-new__lead {
    color: rgba(255, 255, 255, .9);
    font-size: 1rem;
    margin: 8px 0 0;
  }

  .sf-new__toolbar {
    margin-top: 24px;
  }




  .sf-new__jump {
    align-items: center;
    display: flex;
    gap: 12px;
    padding: 10px 16px;
  }

  .sf-new__jump-label {
    color: var(--sf-text-secondary);
    flex: 0 0 auto;
    font-size: .75rem;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
  }

  .sf-new__jump-chips {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 2px 0;
    scrollbar-width: thin;
  }

  .sf-new__jump-chip {
    cursor: pointer;
    flex: 0 0 auto;
  }

  .sf-new__group {
    margin-top: 40px;
    scroll-margin-top: 140px;
  }

  .sf-new__group-head {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px 24px;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .sf-new__supplier {
    display: inline-flex;
    text-decoration: none;
  }

  .sf-new__logo {
    display: block;
    max-height: 56px;
    max-width: 200px;
    width: auto;
  }

  .sf-new__title {
    color: var(--sf-text);
    font-size: 1.375rem;
    font-weight: 700;
    letter-spacing: -.02em;
    margin: 0;
  }

  .sf-new__group-meta {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .sf-new__group-count {
    font-size: .875rem;
  }

  @media (max-width: 599px) {
    .sf-new {
      padding-top: 16px;
    }

    .sf-new__toolbar {
      position: static;
    }
  }
</style>

<i18n>
  {
    "de": {
      "newProducts": "Neuheiten",
      "period1": "Zeitraum 1",
      "period2": "Zeitraum 2",
      "search": "Lieferant oder Artikel suchen",
      "summary": "{count} neue Artikel von {suppliers} Lieferanten – klicken Sie auf einen Artikel, um direkt in den Ordersatz zu gelangen.",
      "counts": "{count} Artikel · {suppliers} Lieferanten",
      "jumpTo": "Springe zu",
      "noResults": "Keine Neuheiten gefunden.",
      "productCount": "{count} Artikel | {count} Artikel | {count} Artikel",
      "toOrderSheet": "Zum Ordersatz"
    },
    "en": {
      "newProducts": "New products",
      "period1": "Period 1",
      "period2": "Period 2",
      "search": "Search supplier or product",
      "summary": "{count} new products from {suppliers} suppliers – click a product to open the order sheet.",
      "counts": "{count} products · {suppliers} suppliers",
      "jumpTo": "Jump to",
      "noResults": "No new products found.",
      "productCount": "{count} products | {count} product | {count} products",
      "toOrderSheet": "Open order sheet"
    }
  }
</i18n>